import { useState } from 'react'
import {
  AspectChip,
  Button,
  Card,
  IconButton,
  SectionLabel,
  Textarea,
} from '../../../design/components'
import { aspectChips, type AspectsSection } from '../types'
import type { CharacterEditing } from '../useCharacterEditing'
import styles from './sections.module.css'

/**
 * Spec §5.1: High Concept and Trouble get their own labelled lines, the rest are
 * chips. First card on the sheet, because aspects drive the whole game.
 *
 * Empty slots are simply absent. A non-owner may be looking at a
 * visibility-filtered copy, and the client cannot — and must not try to — tell
 * "hidden from you" from "never filled in".
 */
export function AspectsCard({
  aspects,
  editing,
}: {
  aspects: AspectsSection | undefined
  editing?: CharacterEditing | undefined
}) {
  const [open, setOpen] = useState(false)

  if (aspects === undefined) return null

  const canEdit = editing?.canEdit === true
  const chips = aspectChips(aspects)

  if (canEdit && editing !== undefined && open) {
    return <AspectsForm aspects={aspects} editing={editing} onClose={() => setOpen(false)} />
  }

  return (
    <Card
      variant="aspects"
      label="Aspects"
      action={
        canEdit ? (
          <IconButton label="Edit aspects" icon="edit" onClick={() => setOpen(true)} />
        ) : undefined
      }
    >
      <NamedAspect label="High Concept" text={aspects.highConcept} />
      <NamedAspect label="Trouble" text={aspects.trouble} trouble />

      {chips.length > 0 ? (
        <div className={styles.chips} style={{ marginTop: 'var(--sp-4)' }}>
          {chips.map((chip) => (
            <AspectChip
              key={chip.key}
              text={chip.text}
              variant={chip.temporary ? 'consequence' : 'neutral'}
            />
          ))}
        </div>
      ) : (
        <p className={styles.empty} style={{ marginTop: 'var(--sp-4)' }}>
          No other aspects.
        </p>
      )}
    </Card>
  )
}

/**
 * The aspects endpoint replaces the whole set, so the form must post every slot —
 * sending only the changed one would blank the rest.
 */
const SLOTS = [
  ['highConcept', 'High Concept'],
  ['trouble', 'Trouble'],
  ['aspect1', 'Aspect 1'],
  ['aspect2', 'Aspect 2'],
  ['aspect3', 'Aspect 3'],
  ['aspect4', 'Aspect 4'],
  ['aspect5', 'Aspect 5'],
  ['aspect6', 'Aspect 6'],
  ['tempAspect1', 'Temporary 1'],
  ['tempAspect2', 'Temporary 2'],
  ['tempAspect3', 'Temporary 3'],
] as const

type SlotKey = (typeof SLOTS)[number][0]

function AspectsForm({
  aspects,
  editing,
  onClose,
}: {
  aspects: AspectsSection
  editing: CharacterEditing
  onClose: () => void
}) {
  const [values, setValues] = useState<Record<SlotKey, string>>(() => {
    const initial = {} as Record<SlotKey, string>
    for (const [key] of SLOTS) initial[key] = aspects[key] ?? ''
    return initial
  })

  const pending = editing.aspects.isPending

  return (
    <Card variant="aspects" label="Edit aspects">
      <form
        className={styles.inlineForm}
        onSubmit={(event) => {
          event.preventDefault()
          const body: Record<string, string | null> = {}
          for (const [key] of SLOTS) body[key] = values[key].trim() === '' ? null : values[key]
          editing.aspects.mutate(body, { onSuccess: onClose })
        }}
      >
        {SLOTS.map(([key, label]) => (
          <Textarea
            key={key}
            label={label}
            value={values[key]}
            onChange={(event) => setValues((prev) => ({ ...prev, [key]: event.target.value }))}
          />
        ))}

        <div className={styles.actions}>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" disabled={pending}>
            {pending ? 'Saving…' : 'Save aspects'}
          </Button>
        </div>
      </form>
    </Card>
  )
}

function NamedAspect({
  label,
  text,
  trouble = false,
}: {
  label: string
  text: string | null
  trouble?: boolean
}) {
  return (
    <div className={`${styles.namedAspect} ${trouble ? styles.trouble : ''}`}>
      <SectionLabel tone="muted" micro>
        {label}
      </SectionLabel>
      {text !== null && text.trim() !== '' ? (
        <span className={styles.namedAspectText}>{text}</span>
      ) : (
        <span className={styles.empty}>Not set</span>
      )}
    </div>
  )
}
